/**
 * Time Service (Web)
 *
 * Owns time-record CRUD, employee status, missed clock-out detection, and
 * bulk approval updates at the record level.
 *
 * Extracted from src/firebase.js per the service-decomposition plan
 * (docs/architecture/scalability-architecture.md §4). firebase.js retains
 * temporary re-export shims marked `// TODO remove shim — refactor:timeService`.
 *
 * Public API:
 *   createTimeRecord(recordData, opts)
 *   getTimeRecords({ employeeId, startDate, endDate, companyId })
 *   correctTimeRecord(recordId, correctionData, companyId)
 *   updateTimeRecord(recordId, updateData, companyId)
 *   updateTimeRecordApproval(identifier, approvalData, companyId)
 *   getApprovalStatus(employeeId, startDate, endDate, companyId)
 *   bulkUpdateApprovals(employeeId, dateRange, approvalData, companyId)
 *   updateEmployeeStatus(employeeId, statusData)
 *   getEmployeeStatus(employeeId)
 *   getAllEmployeeStatuses(companyId)
 *   detectMissedClockOuts(companyId, date)
 *   getMissedClockOuts(companyId)
 *   resolveMissedClockOut(recordId, clockOutTime, resolvedBy, companyId)
 *
 * Cross-cutting rules:
 *   - Tenant scoping enforced by Firebase rules; this service trusts auth context.
 *   - sourceOfTruthTimestamp is NEVER modified after initial write.
 *   - recomputeAggregates is triggered best-effort (dynamic import, fire-and-forget).
 *   - Event emission is best-effort (dynamic import, fire-and-forget).
 *   - approvalService.markEditedAfterApproval write-through preserved exactly.
 */

import {
  ref,
  set,
  get,
  push,
  query,
  orderByChild,
  equalTo,
  update,
  startAt,
  endAt,
} from 'firebase/database';
import { getFirebase } from '../firebase';
import { detectUserCompany } from '../utils/companyUtils';
import {
  applyTimestampRounding,
  ceilTimestampToQuarterHour,
} from '../utils/timeRounding';
import { formatLocalYMD } from '../utils/timezone';

// Dev-only logger — no-op in production builds.
const dbg = process.env.NODE_ENV !== 'production' ? console.log.bind(console) : () => {};

// ─── Time Records ─────────────────────────────────────────────────────────────

/**
 * @param {Object} recordData - Time record fields. companyId sourced here from recordData or
 *   auto-detected; do NOT trust client-supplied jobId without auth-scoped companyId.
 * @param {Object} [opts]
 * @param {string|null} [opts.jobId=null] - Associate punch with a job. Non-empty string or null.
 *   Job existence is not verified here (avoids a round-trip; rules harden this later).
 * @param {string} [opts.actorId] - auth.uid of the person creating the record.
 *   When an admin creates a record on behalf of a worker, employeeId !== auth.uid, so
 *   the RTDB rule (database.rules.json:329) requires the event userId === auth.uid.
 *   Pass the authenticated user's uid here for admin paths.  Self-service paths
 *   (employeeId === auth.uid) continue to work when actorId is omitted — the emit
 *   falls back to createdBy → employeeId which equals auth.uid in that case.
 */
export const createTimeRecord = async (recordData, { jobId = null, actorId = null } = {}) => {
  if (jobId !== null && (typeof jobId !== 'string' || jobId.trim().length === 0)) {
    throw new Error('createTimeRecord: jobId must be a non-empty string when provided');
  }

  const { database } = getFirebase();

  // Prefer explicit companyId provided by caller (e.g., EmployerDashboard), fallback to detection
  let targetCompanyId = (recordData && recordData.companyId && recordData.companyId !== 'legacy')
    ? recordData.companyId
    : null;

  if (!targetCompanyId) {
    const companyInfo = await detectUserCompany(recordData.employeeId);
    targetCompanyId = companyInfo ? companyInfo.companyId : 'legacy';
  }

  // Get appropriate path for time records - always use company-specific path
  const timeRecordsPath = `companies/${targetCompanyId}/timeRecords`;
  const recordsRef = ref(database, timeRecordsPath);
  const newRecordRef = push(recordsRef);
  const recordId = newRecordRef.key;

  // For manual entries, use the provided timestamp
  const isManualEntry = recordData.manualEntry === true;
  const rawTimestamp = isManualEntry && recordData.timestamp ? recordData.timestamp : new Date().toISOString();

  // SOURCE OF TRUTH: Store the exact original timestamp - NEVER modify this
  const sourceOfTruthTimestamp = rawTimestamp;

  // Apply timestamp rounding based on company settings
  dbg('🕐 Source of truth timestamp:', sourceOfTruthTimestamp, 'Company:', targetCompanyId);
  dbg('🕐 Before timestamp rounding:', rawTimestamp, 'Company:', targetCompanyId);
  dbg('🕐 Is manual entry:', isManualEntry);

  let roundedTimestamp;

  // SKIP rounding for manual entries - employer explicitly entered the time they want
  // Rounding can cause timezone issues with ISO strings and break overnight shift grouping
  if (isManualEntry) {
    dbg('🕐 SKIPPING timestamp rounding for manual entry');
    roundedTimestamp = rawTimestamp;
  } else {
    roundedTimestamp = await applyTimestampRounding(rawTimestamp, targetCompanyId);

    // Company-specific rule: initial clock-in rounds UP to next quarter hour
    // to avoid paying before production starts
    if (targetCompanyId === 'company-1757441106691' && recordData.action === 'clock-in') {
      roundedTimestamp = ceilTimestampToQuarterHour(rawTimestamp);
    }
  }

  dbg('🕐 After timestamp rounding:', roundedTimestamp);

  // Create readable time format from rounded timestamp
  const timeDate = new Date(roundedTimestamp);
  const readableTime = timeDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Chicago'
  });

  dbg('🕐 Final readable time:', readableTime);

  const record = {
    id: recordId,
    ...recordData,
    sourceOfTruthTimestamp: sourceOfTruthTimestamp, // NEVER modify this - permanent record of actual scan time
    timestamp: roundedTimestamp, // Use rounded timestamp for processing
    originalTimestamp: rawTimestamp, // Store unrounded original for reference
    time: readableTime, // Add readable time field from rounded timestamp
    status: 'active', // active, corrected, void
    companyId: targetCompanyId // Track which company this record belongs to
  };

  // Persist jobId only when explicitly provided (keeps records clean for non-job punches)
  if (jobId !== null) {
    record.jobId = jobId;
  }

  await set(newRecordRef, record);

  // Update employee current status only for non-manual entries
  if (!isManualEntry && recordData.employeeId && recordData.action) {
    await updateEmployeeStatus(recordData.employeeId, {
      currentAction: recordData.action,
      lastUpdate: roundedTimestamp, // Use rounded timestamp for consistency
      location: recordData.location || null
    });
  }

  // Best-effort: update job actualLaborHours aggregate when this record has a jobId.
  // Dynamic import avoids the circular dependency (jobService → recruiterApi → firebase).
  if (record.jobId && targetCompanyId) {
    import('./jobService').then(({ recomputeAggregates }) => {
      recomputeAggregates(targetCompanyId, record.jobId).catch((err) => {
        console.warn('createTimeRecord: recomputeAggregates failed (non-fatal)', err);
      });
    }).catch((err) => {
      console.warn('createTimeRecord: failed to load jobService for recomputeAggregates', err);
    });
  }

  // Best-effort: emit TIME_CLOCKED_IN / TIME_CLOCKED_OUT event for SOC 2 PI1.
  // Dynamic import avoids firebase.js → recruiterApi → firebase.js circular dep.
  //
  // P4-H-1: userId must equal auth.uid (database.rules.json:329). The fallback
  // chain is: actorId (explicit auth.uid from caller) → record.createdBy →
  // record.employeeId. Self-service paths work without passing actorId because
  // employeeId === auth.uid. Admin paths (employeeId !== auth.uid) MUST pass
  // actorId or the event write will be silently rejected by the RTDB rule.
  if (record.employeeId && targetCompanyId && targetCompanyId !== 'legacy') {
    import('./eventService').then(({ emit: emitEvent, EVENT_TYPES }) => {
      const eventType = record.action === 'clock-out'
        ? EVENT_TYPES.TIME_CLOCKED_OUT
        : EVENT_TYPES.TIME_CLOCKED_IN;
      const eventActorId = actorId || record.createdBy || record.employeeId;
      emitEvent(
        eventType,
        {
          companyId: targetCompanyId,
          entityType: 'timeRecord',
          entityId: recordId,
          metadata: {
            employeeId: record.employeeId,
            action: record.action || null,
            jobId: record.jobId || null,
            isManualEntry,
          },
        },
        { userId: eventActorId }
      ).catch(() => {});
    }).catch(() => {});
  }

  return record;
};

export const getTimeRecords = async ({ employeeId, startDate, endDate, companyId = null }) => {
  try {
    dbg('Fetching time records for employee:', employeeId);
    dbg('Date range:', startDate, 'to', endDate);

    const { database } = getFirebase();

    // Determine which company's data to fetch
    let targetCompanyId = companyId;
    if (!targetCompanyId && employeeId) {
      // Auto-detect employee's company
      const companyInfo = await detectUserCompany(employeeId);
      targetCompanyId = companyInfo ? companyInfo.companyId : 'legacy';
    }

    // Default to legacy if no company determined
    if (!targetCompanyId) {
      targetCompanyId = 'legacy';
    }

    // Get appropriate path for time records - always use company-specific path
    const timeRecordsPath = `companies/${targetCompanyId}/timeRecords`;
    const timeRecordsRef = ref(database, timeRecordsPath);

    // PERFORMANCE: Use server-side filtering when possible
    // Firebase indexes are defined in database.rules.json for employeeId and timestamp
    let queryRef = timeRecordsRef;
    let useServerFiltering = false;

    // If we have a specific employeeId, use server-side query (most common case)
    if (employeeId) {
      queryRef = query(timeRecordsRef, orderByChild('employeeId'), equalTo(employeeId));
      useServerFiltering = true;
      dbg(`[getTimeRecords] Using server-side filter for employeeId: ${employeeId}`);
    } else if (startDate && endDate) {
      // For date range without employeeId, use timestamp-based server filtering
      // Convert dates to ISO timestamps for comparison
      const startTimestamp = new Date(startDate + 'T00:00:00').toISOString();
      const endTimestamp = new Date(endDate + 'T23:59:59').toISOString();
      queryRef = query(timeRecordsRef, orderByChild('timestamp'), startAt(startTimestamp), endAt(endTimestamp));
      useServerFiltering = true;
      dbg(`[getTimeRecords] Using server-side filter for date range: ${startDate} to ${endDate}`);
    }

    const snapshot = await get(queryRef);
    const records = [];

    if (snapshot.exists()) {
      snapshot.forEach((childSnapshot) => {
        const record = childSnapshot.val();

        // If server filtering was used for employeeId, still need to filter by date client-side
        if (useServerFiltering && employeeId && (startDate || endDate)) {
          const recordDateStr = formatLocalYMD(record.timestamp);
          if (startDate && recordDateStr < startDate) return;
          if (endDate && recordDateStr > endDate) return;
        }

        records.push({
          id: childSnapshot.key,
          ...record
        });
      });
    }

    dbg(`Found ${records.length} records matching criteria for company ${targetCompanyId}`);
    return records;
  } catch (error) {
    console.error('Error fetching time records:', error);
    throw error;
  }
};

/**
 * @param {string} recordId
 * @param {Object} correctionData
 * @param {string} companyId - Required.
 * @param {Object} [opts]
 * @param {string} [opts.actorId] - auth.uid of the person applying the correction.
 *   P4-H-1: must be auth.uid so the RTDB event-write rule passes.  Falls back to
 *   correctionData.changedBy → existingRecord.employeeId for self-service paths.
 */
export const correctTimeRecord = async (recordId, correctionData, companyId = null, { actorId = null } = {}) => {
  const { database } = getFirebase();

  // Require companyId for company-specific path
  if (!companyId) {
    throw new Error('companyId is required for correctTimeRecord');
  }

  const recordRef = ref(database, `companies/${companyId}/timeRecords/${recordId}`);
  const snapshot = await get(recordRef);
  const record = snapshot.val();

  if (!record) throw new Error('Record not found in company path');

  const correctedRecord = {
    ...record,
    ...correctionData,
    status: 'corrected',
    correctionTimestamp: new Date().toISOString(),
    originalRecord: { ...record }
  };

  await set(recordRef, correctedRecord);

  // Best-effort: update job actualLaborHours aggregate when this record has a jobId.
  if (correctedRecord.jobId && companyId) {
    import('./jobService').then(({ recomputeAggregates }) => {
      recomputeAggregates(companyId, correctedRecord.jobId).catch((err) => {
        console.warn('correctTimeRecord: recomputeAggregates failed (non-fatal)', err);
      });
    }).catch((err) => {
      console.warn('correctTimeRecord: failed to load jobService for recomputeAggregates', err);
    });
  }

  // Best-effort: emit TIME_EDITED event for SOC 2 PI1.
  // P4-H-1: userId must equal auth.uid. Fallback chain: actorId → changedBy →
  // employeeId. Self-service paths work without actorId (employeeId === auth.uid).
  if (correctedRecord.employeeId && companyId && companyId !== 'legacy') {
    import('./eventService').then(({ emit: emitEvent, EVENT_TYPES }) => {
      const eventActorId = actorId || correctionData.changedBy || correctedRecord.employeeId;
      emitEvent(
        EVENT_TYPES.TIME_EDITED,
        {
          companyId,
          entityType: 'timeRecord',
          entityId: recordId,
          metadata: {
            employeeId: correctedRecord.employeeId,
            jobId: correctedRecord.jobId || null,
            correctionType: 'correct',
          },
        },
        { userId: eventActorId }
      ).catch(() => {});
    }).catch(() => {});
  }

  return correctedRecord;
};

/**
 * Update specific time record - requires companyId for symmetric data paths.
 *
 * @param {string} recordId
 * @param {Object} updateData - Partial fields to merge.
 * @param {string} companyId - Required; sourced from authenticated session.
 * @param {Object} [opts]
 * @param {string} [opts.actorId] - auth.uid of the person making the edit.
 *   P4-H-1: used as the `userId` in the TIME_EDITED event so the RTDB rule
 *   (database.rules.json:329) passes.  Fallback chain: actorId → updatedBy →
 *   existingRecord.employeeId.  Admin edit paths MUST pass actorId.
 */
export const updateTimeRecord = async (recordId, updateData, companyId = null, { actorId = null } = {}) => {
  try {
    const { database } = getFirebase();

    if (!recordId) {
      console.error('updateTimeRecord called with invalid recordId:', recordId);
      throw new Error(`Invalid record ID: ${recordId}`);
    }

    if (!companyId) {
      console.error('updateTimeRecord called without companyId - this is required for data consistency');
      throw new Error('companyId is required for updateTimeRecord');
    }

    dbg('updateTimeRecord - Updating record:', recordId, 'for company:', companyId);

    const recordRef = ref(database, `companies/${companyId}/timeRecords/${recordId}`);
    const snapshot = await get(recordRef);

    if (!snapshot.exists()) {
      console.error(`Time record not found: companies/${companyId}/timeRecords/${recordId}`);
      throw new Error(`Time record not found with ID: ${recordId}`);
    }

    const existingRecord = snapshot.val();

    // ── Approval write-through shim ───────────────────────────────────────────
    // Service-layer migration shim per docs/architecture/approval-state.md §"Service-layer migration shim".
    //
    // If the record's current canonical approval state is 'approved' AND the
    // incoming patch touches any material field (clockIn, clockOut, etc.),
    // automatically demote to 'edited-after-approval' BEFORE applying the edit.
    // This closes the silent-stale-approval drift: a record cannot remain
    // "approved" after its content is changed without an admin re-reviewing.
    //
    // The demote fires best-effort: if the approval transition fails (e.g. the
    // record was concurrently already demoted), we log a warning but do not block
    // the content update, because losing the edit would be worse than a missed
    // demote (audit trail still captures TIME_EDITED below).
    //
    // TODO: remove this shim after the migration script has run against prod,
    // all known approval writers have been migrated to transitionApproval(),
    // and a one-week observation window confirms zero records with only legacy
    // approval fields.
    if (process.env.NODE_ENV !== 'production') {
      // Dev diagnostic: warn when the write-through fires so we can identify
      // and fix remaining legacy approval writers.
      const willDemote = (() => {
        try {
          const { canonicalApproval: _ca, patchTouchesMaterialFields: _pm } =
            require('./approvalService');
          return _ca(existingRecord) === 'approved' && _pm(updateData);
        } catch { return false; }
      })();
      if (willDemote) {
        console.warn(
          '[updateTimeRecord] approval write-through fired — record was approved ' +
          'and a material field was edited. Demoting to edited-after-approval. ' +
          'Fix the calling code to use transitionApproval() instead of direct edits.'
        );
      }
    }
    import('./approvalService').then(
      ({ canonicalApproval, patchTouchesMaterialFields, markEditedAfterApproval }) => {
        if (
          canonicalApproval(existingRecord) === 'approved' &&
          patchTouchesMaterialFields(updateData)
        ) {
          const editorId = actorId || updateData.updatedBy || existingRecord.employeeId;
          if (editorId) {
            markEditedAfterApproval(companyId, recordId, { by: editorId }).catch((err) => {
              console.warn('updateTimeRecord: markEditedAfterApproval failed (non-fatal)', err);
            });
          }
        }
      }
    ).catch(() => {});
    // ── End approval write-through shim ──────────────────────────────────────

    const updatedRecord = {
      ...existingRecord,
      ...updateData,
      updatedAt: new Date().toISOString()
    };

    await set(recordRef, updatedRecord);
    dbg(`Successfully updated time record ${recordId}`);

    // Best-effort: update job actualLaborHours aggregate when this record has a jobId.
    if (updatedRecord.jobId && companyId) {
      import('./jobService').then(({ recomputeAggregates }) => {
        recomputeAggregates(companyId, updatedRecord.jobId).catch((err) => {
          console.warn('updateTimeRecord: recomputeAggregates failed (non-fatal)', err);
        });
      }).catch((err) => {
        console.warn('updateTimeRecord: failed to load jobService for recomputeAggregates', err);
      });
    }

    // Best-effort: emit TIME_EDITED event for SOC 2 PI1.
    // P4-H-1: userId must equal auth.uid. Fallback chain: actorId → updatedBy →
    // existingRecord.employeeId. Admin edit paths MUST pass actorId.
    if (updatedRecord.employeeId && companyId && companyId !== 'legacy') {
      import('./eventService').then(({ emit: emitEvent, EVENT_TYPES }) => {
        const eventActorId = actorId || updateData.updatedBy || existingRecord.employeeId;
        if (eventActorId) {
          emitEvent(
            EVENT_TYPES.TIME_EDITED,
            {
              companyId,
              entityType: 'timeRecord',
              entityId: recordId,
              metadata: {
                employeeId: updatedRecord.employeeId,
                jobId: updatedRecord.jobId || null,
                correctionType: 'update',
                ...(updateData.updatedBy ? { updatedBy: updateData.updatedBy } : {}),
              },
            },
            { userId: eventActorId }
          ).catch(() => {});
        }
      }).catch(() => {});
    }

    return updatedRecord;
  } catch (error) {
    console.error('Error updating time record:', error);
    throw error;
  }
};

// Time Record Approval Management - requires companyId for symmetric data paths
export const updateTimeRecordApproval = async (identifier, approvalData, companyId = null) => {
  try {
    const { database } = getFirebase();
    let updates = {};

    if (!companyId) {
      throw new Error('companyId is required for updateTimeRecordApproval');
    }

    const timeRecordsPath = `companies/${companyId}/timeRecords`;

    dbg('updateTimeRecordApproval - Using path:', timeRecordsPath);

    // If identifier is an object with employeeId and date
    if (typeof identifier === 'object' && identifier.employeeId && identifier.date) {
      const { employeeId, date } = identifier;

      dbg('Updating approval for employee:', employeeId, 'on date:', date);

      // Get all time records from the company-specific path
      const timeRecordsRef = ref(database, timeRecordsPath);
      const snapshot = await get(timeRecordsRef);

      if (snapshot.exists()) {
        const records = snapshot.val();
        const recordsArray = Object.keys(records).map(key => ({ ...records[key], id: key }));

        // Import the work day utility function
        const { getRecordWorkDay } = require('../utils/overnightShiftUtils');

        // Find matching records using proper work day logic and prepare updates
        Object.keys(records).forEach(key => {
          const record = records[key];
          if (record.employeeId === employeeId) {
            // Use getRecordWorkDay to properly determine which work day this record belongs to
            const recordWorkDay = getRecordWorkDay(record, recordsArray, companyId);
            if (recordWorkDay === date) {
              updates[`${timeRecordsPath}/${key}`] = {
                ...record,
                ...approvalData
              };
            }
          }
        });
      }
    } else if (typeof identifier === 'string') {
      // If identifier is a direct record ID or key
      const recordRef = ref(database, `${timeRecordsPath}/${identifier}`);
      const snapshot = await get(recordRef);

      if (snapshot.exists()) {
        const record = snapshot.val();
        updates[`${timeRecordsPath}/${identifier}`] = {
          ...record,
          ...approvalData
        };
      }
    } else {
      throw new Error('Invalid identifier format. Expected object with {employeeId, date} or string recordId');
    }

    if (Object.keys(updates).length === 0) {
      throw new Error('No records found to update');
    }

    dbg('Applying updates:', updates);

    // Apply updates to database
    await update(ref(database), updates);

    dbg('Successfully updated time record approvals');
    return true;

  } catch (error) {
    console.error('Error updating time record approval:', error);
    throw error;
  }
};

// Get approval status for a specific employee and date range.
// companyId is required — callers that previously omitted it relied on the
// legacy top-level /timeRecords path which is pre-Phase-0 and no longer written.
export const getApprovalStatus = async (employeeId, startDate, endDate, companyId) => {
  if (!companyId) {
    throw new Error('getApprovalStatus: companyId is required');
  }
  try {
    const { database } = getFirebase();
    const timeRecordsRef = ref(database, `companies/${companyId}/timeRecords`);
    const snapshot = await get(timeRecordsRef);

    const approvalStatus = {};

    if (snapshot.exists()) {
      const records = snapshot.val();
      const recordsArray = Object.values(records);

      // Import the work day utility function
      const { getRecordWorkDay } = require('../utils/overnightShiftUtils');

      recordsArray.forEach(record => {
        if (record.employeeId === employeeId) {
          // Use getRecordWorkDay to properly determine which work day this record belongs to
          const recordWorkDay = getRecordWorkDay(record, recordsArray);
          const start = new Date(startDate);
          const end = new Date(endDate);
          const current = new Date(recordWorkDay);

          if (current >= start && current <= end) {
            if (!approvalStatus[recordWorkDay]) {
              approvalStatus[recordWorkDay] = {
                recruiterApproved: null,
                employerApproved: null,
                hasRecords: false
              };
            }

            approvalStatus[recordWorkDay].hasRecords = true;

            if (record.recruiterApproved !== undefined) {
              approvalStatus[recordWorkDay].recruiterApproved = record.recruiterApproved;
              approvalStatus[recordWorkDay].recruiterApprovedAt = record.recruiterApprovedAt;
              approvalStatus[recordWorkDay].recruiterApprovedBy = record.recruiterApprovedBy;
            }

            if (record.employerApproved !== undefined) {
              approvalStatus[recordWorkDay].employerApproved = record.employerApproved;
              approvalStatus[recordWorkDay].employerApprovedAt = record.employerApprovedAt;
              approvalStatus[recordWorkDay].employerApprovedBy = record.employerApprovedBy;
            }
          }
        }
      });
    }

    return approvalStatus;

  } catch (error) {
    console.error('Error getting approval status:', error);
    throw error;
  }
};

// Bulk update approvals for multiple days.
// companyId is required — callers that previously omitted it relied on the
// legacy top-level /timeRecords path which is pre-Phase-0 and no longer written.
export const bulkUpdateApprovals = async (employeeId, dateRange, approvalData, companyId) => {
  if (!companyId) {
    throw new Error('bulkUpdateApprovals: companyId is required');
  }
  try {
    const { database } = getFirebase();
    const timeRecordsPath = `companies/${companyId}/timeRecords`;
    const timeRecordsRef = ref(database, timeRecordsPath);
    const snapshot = await get(timeRecordsRef);

    let updates = {};

    if (snapshot.exists()) {
      const records = snapshot.val();

      Object.keys(records).forEach(key => {
        const record = records[key];
        const recordDate = record.timestamp.split('T')[0];

        if (record.employeeId === employeeId && dateRange.includes(recordDate)) {
          updates[`${timeRecordsPath}/${key}`] = {
            ...record,
            ...approvalData
          };
        }
      });
    }

    if (Object.keys(updates).length === 0) {
      throw new Error('No records found for bulk update');
    }

    await update(ref(database), updates);

    dbg(`Bulk updated ${Object.keys(updates).length} records for employee ${employeeId}`);
    return Object.keys(updates).length;

  } catch (error) {
    console.error('Error in bulk update approvals:', error);
    throw error;
  }
};

// ─── Employee Status ──────────────────────────────────────────────────────────

export const updateEmployeeStatus = async (employeeId, statusData) => {
  try {
    const { database } = getFirebase();

    // Detect employee's company
    const companyInfo = await detectUserCompany(employeeId);
    const companyId = companyInfo ? companyInfo.companyId : 'legacy';

    // Get appropriate path for employee status - always use company-specific path
    const statusPath = `companies/${companyId}/employeeStatus/${employeeId}`;
    const statusRef = ref(database, statusPath);

    const statusUpdate = {
      ...statusData,
      updatedAt: new Date().toISOString()
    };

    await set(statusRef, statusUpdate);
    dbg(`Updated status for employee ${employeeId} in company ${companyId}:`, statusUpdate);
    return statusUpdate;
  } catch (error) {
    console.error('Error updating employee status:', error);
    throw error;
  }
};

export const getEmployeeStatus = async (employeeId) => {
  try {
    const { database } = getFirebase();

    // Detect employee's company
    const companyInfo = await detectUserCompany(employeeId);
    const companyId = companyInfo ? companyInfo.companyId : 'legacy';

    // Get appropriate path for employee status - always use company-specific path
    const statusPath = `companies/${companyId}/employeeStatus/${employeeId}`;
    const statusRef = ref(database, statusPath);
    const snapshot = await get(statusRef);

    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      // Return default status if no status exists
      return {
        currentAction: null,
        lastUpdate: null,
        location: null,
        updatedAt: new Date().toISOString()
      };
    }
  } catch (error) {
    console.error('Error getting employee status:', error);
    throw error;
  }
};

export const getAllEmployeeStatuses = async (companyId = null) => {
  try {
    const { database } = getFirebase();

    // Default to legacy company if not specified
    const targetCompanyId = companyId || 'legacy';

    // Always use company-specific path
    const statusPath = `companies/${targetCompanyId}/employeeStatus`;

    const statusRef = ref(database, statusPath);
    const snapshot = await get(statusRef);

    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      return {};
    }
  } catch (error) {
    console.error('Error getting all employee statuses:', error);
    throw error;
  }
};

// ─── Missed Clock-Out Detection ───────────────────────────────────────────────

/**
 * Detect missed clock-outs for a specific date or date range
 * A missed clock-out occurs when an employee has a clock-in without a matching clock-out
 * by end of day (or shift end time if schedules are configured)
 *
 * This is the centralized detection - both web and mobile read from the same Firebase data
 */
export const detectMissedClockOuts = async (companyId, date = null) => {
  try {
    const { database } = getFirebase();

    // Default to yesterday if no date provided (check previous day's records)
    const checkDate = date || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const timeRecordsPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/timeRecords`
      : 'timeRecords';

    const recordsRef = ref(database, timeRecordsPath);
    const snapshot = await get(recordsRef);

    if (!snapshot.exists()) {
      return { detected: 0, records: [] };
    }

    const allRecords = snapshot.val();
    const recordsArray = Object.entries(allRecords).map(([id, record]) => ({ ...record, id }));

    // Group records by employee and date
    const employeeRecordsByDate = {};

    recordsArray.forEach(record => {
      const recordDate = record.timestamp?.split('T')[0];
      if (!recordDate || recordDate !== checkDate) return;

      const employeeId = record.employeeId;
      if (!employeeId) return;

      const key = `${employeeId}_${recordDate}`;
      if (!employeeRecordsByDate[key]) {
        employeeRecordsByDate[key] = {
          employeeId,
          date: recordDate,
          clockIns: [],
          clockOuts: [],
          records: []
        };
      }

      employeeRecordsByDate[key].records.push(record);

      if (record.action === 'clock-in') {
        employeeRecordsByDate[key].clockIns.push(record);
      } else if (record.action === 'clock-out') {
        employeeRecordsByDate[key].clockOuts.push(record);
      }
    });

    // Find employees with unbalanced clock-ins/clock-outs
    const missedClockOuts = [];
    const updates = {};

    Object.values(employeeRecordsByDate).forEach(data => {
      const { employeeId, date, clockIns, clockOuts, records } = data;

      // Sort by timestamp
      clockIns.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      clockOuts.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // More clock-ins than clock-outs = missed clock-out
      if (clockIns.length > clockOuts.length) {
        // Find the clock-in without a matching clock-out
        // Simple approach: if there's 1 more clock-in than clock-out, flag the last clock-in
        const lastClockIn = clockIns[clockIns.length - 1];

        // Only flag if not already flagged
        if (!lastClockIn.missedClockOut) {
          missedClockOuts.push({
            employeeId,
            date,
            clockInRecord: lastClockIn,
            clockInTime: lastClockIn.timestamp
          });

          // Prepare update to flag the record
          updates[`${timeRecordsPath}/${lastClockIn.id}`] = {
            ...lastClockIn,
            missedClockOut: true,
            missedClockOutDetectedAt: new Date().toISOString()
          };
        }
      }
    });

    // Apply updates to database
    if (Object.keys(updates).length > 0) {
      await update(ref(database), updates);
      dbg(`Flagged ${Object.keys(updates).length} missed clock-outs for date ${checkDate}`);
    }

    return {
      detected: missedClockOuts.length,
      records: missedClockOuts,
      date: checkDate
    };

  } catch (error) {
    console.error('Error detecting missed clock-outs:', error);
    throw error;
  }
};

/**
 * Get all records flagged as missed clock-outs (unresolved)
 */
export const getMissedClockOuts = async (companyId) => {
  try {
    const { database } = getFirebase();

    const timeRecordsPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/timeRecords`
      : 'timeRecords';

    const recordsRef = ref(database, timeRecordsPath);
    const snapshot = await get(recordsRef);

    if (!snapshot.exists()) {
      return [];
    }

    const allRecords = snapshot.val();
    const missedClockOuts = [];

    Object.entries(allRecords).forEach(([id, record]) => {
      if (record.missedClockOut && !record.missedClockOutResolvedAt) {
        missedClockOuts.push({ ...record, id });
      }
    });

    // Sort by date descending (most recent first)
    missedClockOuts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return missedClockOuts;

  } catch (error) {
    console.error('Error getting missed clock-outs:', error);
    throw error;
  }
};

/**
 * Resolve a missed clock-out by adding a manual clock-out time.
 *
 * @param {string} recordId     - The clock-in record that was missed
 * @param {string} clockOutTime - ISO timestamp to use as the clock-out time
 * @param {string} resolvedBy   - uid of the person resolving (typically an admin)
 * @param {string} companyId
 * @param {Object} [opts]
 * @param {string} [opts.actorId] - auth.uid of the resolving actor.  Used as
 *   the userId on the TIME_CLOCKED_OUT event to satisfy P4-H-1.  Falls back to
 *   resolvedBy when omitted (correct when resolvedBy is already auth.uid).
 */
export const resolveMissedClockOut = async (recordId, clockOutTime, resolvedBy, companyId, { actorId = null } = {}) => {
  try {
    const { database } = getFirebase();

    const timeRecordsPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/timeRecords`
      : 'timeRecords';

    // First, get the original clock-in record
    const recordRef = ref(database, `${timeRecordsPath}/${recordId}`);
    const snapshot = await get(recordRef);

    if (!snapshot.exists()) {
      throw new Error('Record not found');
    }

    const clockInRecord = snapshot.val();

    // Update the clock-in record to mark as resolved
    await update(recordRef, {
      missedClockOutResolvedAt: new Date().toISOString(),
      missedClockOutResolvedBy: resolvedBy,
      autoClockOutTime: clockOutTime
    });

    // Create a new clock-out record
    const newClockOutRef = push(ref(database, timeRecordsPath));
    const clockOutRecord = {
      id: newClockOutRef.key,
      employeeId: clockInRecord.employeeId,
      employeeName: clockInRecord.employeeName,
      action: 'clock-out',
      timestamp: clockOutTime,
      manualEntry: true,
      manualEntryReason: 'Resolved missed clock-out',
      manualEntryBy: resolvedBy,
      manualEntryAt: new Date().toISOString(),
      companyId: companyId,
      linkedToMissedClockIn: recordId,
      createdAt: new Date().toISOString()
    };

    await set(newClockOutRef, clockOutRecord);

    // Best-effort: emit TIME_CLOCKED_OUT for the resolved missed-clock-out.
    // P4-H-1: userId must equal auth.uid. Fallback: actorId → resolvedBy.
    // The { resolved: true } flag in metadata distinguishes this from a normal
    // clock-out so downstream consumers (audit, AI) can identify resolutions.
    if (companyId && companyId !== 'legacy' && clockInRecord.employeeId) {
      import('./eventService').then(({ emit: emitEvent, EVENT_TYPES }) => {
        const eventActorId = actorId || resolvedBy;
        if (eventActorId) {
          emitEvent(
            EVENT_TYPES.TIME_CLOCKED_OUT,
            {
              companyId,
              entityType: 'timeRecord',
              entityId: newClockOutRef.key,
              metadata: {
                employeeId: clockInRecord.employeeId,
                resolved: true,
                resolvedBy,
                linkedToMissedClockIn: recordId,
                isManualEntry: true,
              },
            },
            { userId: eventActorId }
          ).catch(() => {});
        }
      }).catch(() => {});
    }

    dbg(`Resolved missed clock-out for record ${recordId}`);
    return clockOutRecord;

  } catch (error) {
    console.error('Error resolving missed clock-out:', error);
    throw error;
  }
};
